import React, {useEffect, useState} from 'react';
import moment from "moment-timezone";
import {CHICAGO_TIMEZONE} from "../Alerts/MissingData/filters";
import Page from "../Components/Page";
import Checkbox from "../Components/Checkbox";
import _ from "lodash";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import {makeApolloClientCall} from "../../Api/makeApolloApiCall";
import {currentWeekIdQuery} from "../SldRelease/SldReleasePage";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import AirflowDagLogs from "../Components/airflow/dagLogs/AirflowDagLogs";
import {labelCurrentPeriodWeek, makeApiCallForPeriods} from "../Components/periodDropdownMethods";
import {getUserEmail} from "../../auth/accessTokenValidator";
import {wrapValueInOptionStructure, wrapValueLabelInOptionStructure} from "../../utils/maybeAddLabelToOptions";
import {makeApiCall} from "../../Api/makeApiCall";
import {AVAILABLE_PROJECTION_VERSIONS_QUERY} from "../Projections/Components/VersionSelect";
import PiEngineSnapshotVersionSelect, {
    mapPiEngineSnapshotVersionsToOptions,
    PI_ENGINE_SNAPSHOT_VERSIONS_QUERY
} from "./PiEngineSnapshotVersionSelect";
import {Breadcrumbs} from "../Components/Breadcrumbs";
import {toastError, toastSuccess} from "../../utils/toast";
import {invokeDAGviaApiPost} from "../../Api/invokeDag";
import {toast} from "react-toastify";

const PERIOD_DAG_NAME = "publish_adp_period_release";
const WEEKLY_DAG_NAME = "publish_adp_weekly_release";


const RELEASE_TYPE_OPTIONS = [
    {label: "Period", value: PERIOD_DAG_NAME},
    {label: "Weekly", value: WEEKLY_DAG_NAME}
]

const WEEK_NUMBER_OPTIONS = [
    {label: 1, value: 1},
    {label: 2, value: 2},
    {label: 3, value: 3},
    {label: 4, value: 4}
]

export const ERROR_TOAST_OPTIONS = {autoClose: 5000};

const AdpPrivateCloudSnowflakeUpload = ({pageName = "Advanced Data Platform Publish", extraDagConfig = {}}) => {

    const [loading, setLoading] = useState(false);

    const [snowflakeSchemaName, setSnowflakeSchemaName] = useState(undefined);
    const [selectedReleaseType, setSelectedReleaseType] = useState(PERIOD_DAG_NAME);
    const [selectedWeekId, setSelectedWeekId] = useState(undefined);
    const [selectedWeekNumber, setSelectedWeekNumber] = useState(undefined);
    const [selectedMarketPeriod, setSelectedMarketPeriod] = useState(undefined);
    const [promoCheckpointPath, setPromoCheckpointPath] = useState(undefined);
    const [stacklinePromoCheckpointPath, setStacklinePromoCheckpointPath] = useState(undefined);
    const [isRefreshPeriod, setIsRefreshPeriod] = useState(false);
    const [createCheckpoint, setCreateCheckpoint] = useState(false);
    const [checkpointProjectionVersion, setCheckpointProjectionVersion] = useState(undefined);
    const [projectionCheckpoint, setProjectionCheckpoint] = useState(undefined);
    const [piEngineSnapshotVersion, setPiEngineSnapshotVersion] = useState(undefined);

    const [marketPeriodOptions, setMarketPeriodOptions] = useState([]);
    const [weekIdOptions, setWeekIdOptions] = useState([]);
    const [promoCheckpointOptions, setPromoCheckpointOptions] = useState([]);
    const [stacklinePromoCheckpointOptions, setStacklinePromoCheckpointOptions] = useState([]);
    const [availableProjectionVersionsOptions, setAvailableProjectionVersionsOptions] = useState([]);
    const [checkpointProjectionCheckpointOptions, setCheckpointProjectionCheckpointOptions] = useState([]);
    const [piEngineSnapshotVersionOptions, setPiEngineSnapshotVersionOptions] = useState([]);

    const [userSelectedOptionsIssues, setUserSelectedOptionsIssues] = useState([]);

    const isPeriodRelease = selectedReleaseType === PERIOD_DAG_NAME;

    const getCheckpointQuery = (checkpoint) => {
        const datasetParam = checkpoint.datasets.map(dataset => `"p_dataset=${dataset}"`).join(',');
        const tableDataParam = checkpoint.tableData.map(dataset => `"p_data=${dataset}"`).join(',');
        return `query {
                    schemasForCheckpoints(
                        dataset: [${datasetParam}]
                        tableData: [${tableDataParam}]
                        ){
                        checkpointName,
                        checkpointPath,
                        checkpointSchema,
                        dataset
                  }
                }`;
    }

    const checkpointsInfo = {
        stacklinePromo: {
            datasets: ['generate_stackline_measures', 'promo_stackline'],
            tableData: ['stackline_promo_parquet']
        },
        promo: {
            datasets: ['cleanse_fill_extract', 'filtered_promo', 'promo_extract', 'store_insights'],
            tableData: ['promo_parquet']
        }
    }

    const getCheckpointOptions = checkpointResults => checkpointResults
        .schemasForCheckpoints
        .map((value) => wrapValueLabelInOptionStructure(value.checkpointPath, value.checkpointName));

    const getDataBackupOptions = (folder, results) =>
        results.map(result => ({
            label: `data-backup ${result}`,
            value: `gs://spins-edp-${process.env.REACT_APP_ENV}-${folder.bucket}/${folder.subDir}/${result}`
        }))

    const getGcsFolders = (bucketSuffix, subDirectory) => {
        const bucket = `spins-edp-${process.env.REACT_APP_ENV}-${bucketSuffix}`
        return `${process.env.REACT_APP_EDP_API_BASE_URL}/listDirectories/?bucket=${bucket}&subdirectory=${subDirectory}`;
    };

    const dataBackup = {
        promo: {
            bucket: 'data-backup/p_data=publish_adp_private_cloud',
            subDir: `spins-edp-prod-checkpoint/p_dataset=cleanse_fill_extract`
        },
        stackline: {
            bucket: 'data-backup/p_data=publish_adp_private_cloud',
            subDir: `spins-edp-prod-data`
        }
    }

    const getPeriodConfig = () => {
        return {
            is_refresh_period: isRefreshPeriod,
            start_week: Number(selectedWeekId)
        }
    }

    const getWeeklyConfig = () => {
        return {
            week_number: selectedWeekNumber,
        }
    }

    useEffect(() => {
        setLoading(true);
        setSnowflakeSchemaName(`SALES_AND_ECOMM_DATA__${getUserEmail()?.split('@')[0].toUpperCase()}`);
        Promise.all([
            makeApolloClientCall(currentWeekIdQuery, true),
            makeApolloClientCall('{ periods( isAdpStart: true) { weekId } }'),
            makeApolloClientCall('{ periods(periodEndWeeksOnly: true) { periodAbbreviation endDate } }'),
            makeApiCallForPeriods(),
            makeApolloClientCall(getCheckpointQuery(checkpointsInfo.stacklinePromo)),
            makeApolloClientCall(getCheckpointQuery(checkpointsInfo.promo)),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/projectionsQcCheckpoints`),
            makeApiCall(getGcsFolders(dataBackup.promo.bucket, dataBackup.promo.subDir)),
            makeApolloClientCall(AVAILABLE_PROJECTION_VERSIONS_QUERY),
            makeApolloClientCall(PI_ENGINE_SNAPSHOT_VERSIONS_QUERY),

        ])
            .then(([
                       currentWeekIdQueryResults,
                       adpWeekQueryResults,
                       marketPeriodResults,
                       periodsResults,
                       stacklinePromoCheckpointResults,
                       promoCheckpointResults,
                       projectionCheckpointResults,
                       dataBackupPromoResults,
                       availableProjectionVersions,
                       piEngineSnapshotVersionResults
                   ]) => {

                const currentAdpWeek = adpWeekQueryResults.periods?.[0]?.weekId;
                const currentPeriodAbbreviation = currentWeekIdQueryResults.currentPeriodQuad?.[0]?.periodAbbreviation;
                const currentPeriodEndDate = currentWeekIdQueryResults.currentPeriodQuad?.[0]?.endDate;
                setSelectedWeekId(currentAdpWeek);
                setSelectedMarketPeriod(currentPeriodAbbreviation);

                const nextPeriodEndDate = _.minBy(
                    marketPeriodResults.periods.filter(mp => mp.endDate > currentPeriodEndDate),
                    'endDate'
                )?.endDate;
                const marketPeriodOptions = marketPeriodResults.periods
                    .filter(mp => mp.endDate <= (nextPeriodEndDate ?? currentPeriodEndDate))
                    .sort((a, b) => b.periodAbbreviation.localeCompare(a.periodAbbreviation))
                    .map(mp => ({...(wrapValueInOptionStructure(mp.periodAbbreviation)), endDate: mp.endDate}));

                setMarketPeriodOptions(marketPeriodOptions);

                const periodsMap = labelCurrentPeriodWeek(periodsResults)
                setWeekIdOptions(periodsMap)

                const stacklinePromoMap = getCheckpointOptions(stacklinePromoCheckpointResults)
                const stacklineDataBackupMap = [
                    {
                        label: `data-backup stackline_promo_parquet`,
                        value: `gs://spins-edp-${process.env.REACT_APP_ENV}-${dataBackup.stackline.bucket}/${dataBackup.stackline.subDir}`
                    }
                ]
                setStacklinePromoCheckpointOptions( _.concat(stacklineDataBackupMap, stacklinePromoMap));

                const promoMap = getCheckpointOptions(promoCheckpointResults)
                const promoDataBackupMap = getDataBackupOptions(dataBackup.promo, dataBackupPromoResults)

                setPromoCheckpointOptions(_.concat(promoDataBackupMap, promoMap));
                const cleanedProjectionCheckpointResults = (projectionCheckpointResults?.data || [])
                    .map((results  ) => {
                        return {
                            value: results.gcsPath,
                            label: results.checkpoint.replace('p_run_id=', ''),
                            minDate: results.minDate,
                            maxDate: results.maxDate,
                        }
                    })
                setCheckpointProjectionCheckpointOptions(cleanedProjectionCheckpointResults);

                setAvailableProjectionVersionsOptions(availableProjectionVersions?.availableProjectionVersions?.map(v => wrapValueInOptionStructure(v.version)) || []);

                setPiEngineSnapshotVersionOptions(mapPiEngineSnapshotVersionsToOptions(piEngineSnapshotVersionResults));

            })
            .finally(() => setLoading(false));
    }, [])

    const validateUserSelections = () => {
        let issues = []
        const startDate = getSelectedOption(selectedWeekId, weekIdOptions)?.endDate
        const endDate = getSelectedOption(selectedMarketPeriod, marketPeriodOptions)?.endDate
        const qcOption = getSelectedOption(projectionCheckpoint, checkpointProjectionCheckpointOptions)


        const qcMaxDate = qcOption?.maxDate;
        const qcMinDate = qcOption?.minDate;

        if (!isPeriodRelease) {

            if (!selectedWeekNumber) {
                issues.push({id: "week-number-required", text: "Week Number is required."});
            }
            else {
                const weeklyDate = moment.tz(endDate, CHICAGO_TIMEZONE).add(selectedWeekNumber * 7, 'days').format('YYYY-MM-DD');

                if (weeklyDate && qcMinDate && qcMinDate > weeklyDate) {
                    issues.push({id: "start-date-invalid", text: <>Start date must be greater than or equal to the minimum date for the selected Projection Extract. <b>{weeklyDate}</b> vs <b>{qcMinDate}</b></>})
                }
                if (weeklyDate && qcMaxDate && qcMaxDate < weeklyDate) {
                    issues.push({id: "end-date-invalid", text: <>End date must be less than or equal to the maximum date for the selected Projection Extract. <b>{weeklyDate}</b> vs <b>{qcMaxDate}</b></>})
                }
            }
        }
        else {
            if (startDate && qcMinDate && qcMinDate > startDate) {
                issues.push({id: "start-date-invalid", text: <>Start date must be greater than or equal to the minimum date for the selected Projection Extract. <b>{startDate}</b> vs <b>{qcMinDate}</b></>})
            }
            if (endDate && qcMaxDate && qcMaxDate < endDate) {
                issues.push({id: "end-date-invalid", text: <>End date must be less than or equal to the maximum date for the selected Projection Extract. <b>{endDate}</b> vs <b>{qcMaxDate}</b></>})
            }
        }

        if (!snowflakeSchemaName) {
            issues.push({id: "snowflake-schema-name-required", text: "Snowflake Schema Name is required."});
        }

        if (createCheckpoint && !checkpointProjectionVersion) {
            issues.push({id: "extract-projection-version-required", text: "Extract Projection Version is required to create a new extract."});
        }

        if (!projectionCheckpoint && !checkpointProjectionVersion) {
            issues.push({id: "projection-qc-extract-required", text: "Projection QC Extract must be selected when not creating a new extract."});
        }

        if (!piEngineSnapshotVersion && piEngineSnapshotVersionOptions.length > 0 && !piEngineSnapshotVersionOptions.some(option => option.value === selectedMarketPeriod)) {
            issues.push({id: "pi-engine-snapshot-version-required", text: <>No PI Engine Snapshot exists for <b>{selectedMarketPeriod}</b>. Select a PI Engine Snapshot Version.</>});
        }

        setUserSelectedOptionsIssues(issues)
        return issues.length > 0;
    }

    const getIssue = (id) => userSelectedOptionsIssues.find(issue => issue.id === id)?.text;

    const onSubmitButtonClicked = async () => {
        if (validateUserSelections()) {
            return;
        }

        const sharedOptions = {
            period: selectedMarketPeriod,
            snowflake_schema_name: snowflakeSchemaName?.trim() || undefined,
            promo_source_checkpoint_path: promoCheckpointPath,
            stackline_promo_source_checkpoint_path: stacklinePromoCheckpointPath,
            triggered_by: getUserEmail(),
            create_checkpoint: createCheckpoint,
            projection_version: checkpointProjectionVersion,
            ...(projectionCheckpoint !== undefined && {projection_qc_checkpoint_path: projectionCheckpoint}),
            ...(piEngineSnapshotVersion !== undefined && {pi_engine_snapshot_version: piEngineSnapshotVersion}),
        }

        const dagSpecificOptions = isPeriodRelease ? getPeriodConfig() : getWeeklyConfig();
        const dagToTrigger = selectedReleaseType;

        const data = {...sharedOptions, ...dagSpecificOptions, ...extraDagConfig};
        toastSuccess(toast, `${dagToTrigger} DAG config ${JSON.stringify(data, null, 2)}`)
        invokeDAGviaApiPost(dagToTrigger, data)
            .then((response) => {
                if (response?.apiError)
                    toastError(toast, `${dagToTrigger} DAG trigger failed with error ${JSON.stringify(response.apiError)}`, ERROR_TOAST_OPTIONS)
                else
                    toastSuccess(toast, `${dagToTrigger} DAG triggered successfully`)
            })
            .catch(e => {
                toastError(toast, `${dagToTrigger} DAG trigger failed with error ${JSON.stringify(e)}`, ERROR_TOAST_OPTIONS)
            })
    }

    const getSelectedOption = (selectedValue, options) => selectedValue && options?.find(option => option.value.toString() === selectedValue.toString());

    return <Page name={pageName} beta={true}
                 breadCrumbs={<Breadcrumbs items={[{label: "ADP"}, {label: pageName}]}/>}>
        <LoadingWrapper loading={loading}>
            <h5>Configuration Options</h5>
            <div className={"private-cloud-snowflake-upload-container"}>
                <div className={"custom-run-selections"}>
                    <div className={"custom-run-selections-column"}>
                        <div id={'required_column_header'} className={"custom-run-selections-row custom-run-section-header"}>
                            <h6>Required</h6>
                        </div>
                        <div id={'release_type_dropdown_div'} className={"custom-run-selections-row"}>
                            <SelectDropdown id={"release_type_dropdown"}
                                            dataTestId={"release_type_dropdown"}
                                            inputLabel={"Release Type"}
                                            selectValue={selectedReleaseType}
                                            selectLabel={getSelectedOption(selectedReleaseType, RELEASE_TYPE_OPTIONS)?.label}
                                            options={RELEASE_TYPE_OPTIONS}
                                            onChange={(option) => setSelectedReleaseType(option.value)}
                            />
                        </div>
                        <div id={'market_period_dropdown_div'} className={"custom-run-selections-row"}>
                            <SelectDropdown id={"market_period_dropdown"}
                                            dataTestId={"market_period_dropdown"}
                                            inputLabel={"Period"}
                                            selectValue={selectedMarketPeriod}
                                            options={marketPeriodOptions}
                                            onChange={(option) => setSelectedMarketPeriod(option.value)}
                            />
                            {isPeriodRelease && <div id={'check-boxes'}>
                                <Checkbox
                                    id={'is_refresh_period'}
                                    labelText={'Is Refresh Period'}
                                    onChange={setIsRefreshPeriod}
                                    checked={isRefreshPeriod}/>
                            </div>}
                        </div>
                        {isPeriodRelease && <div id={'start_week_dropdown_div'} className={"custom-run-selections-row"}>
                            <SelectDropdown
                                id={'start_week_dropdown'}
                                dataTestId={'start_week_dropdown'}
                                inputLabel={"Start Week ID"}
                                onChange={(option) => setSelectedWeekId(option.value)}
                                selectValue={selectedWeekId && selectedWeekId.toString()}
                                selectLabel={getSelectedOption(selectedWeekId, weekIdOptions)?.label}
                                options={weekIdOptions}
                            />
                        </div>}
                        {!isPeriodRelease && <div id={'week_number_dropdown_div'} className={"custom-run-selections-row"}>
                            <div className={'field-with-error'}>
                                <SelectDropdown
                                    id={'week_number_dropdown'}
                                    dataTestId={'week_number_dropdown'}
                                    inputLabel={"Week Number"}
                                    onChange={(option) => setSelectedWeekNumber(option.value)}
                                    selectValue={selectedWeekNumber}
                                    options={WEEK_NUMBER_OPTIONS}
                                />
                                {getIssue('week-number-required') && <div className={'error'} data-testid={'week-number-required'}>{getIssue('week-number-required')}</div>}
                            </div>
                        </div>}
                        <div id={'snowflake_schema_name_div'} className={"custom-run-selections-row"}>
                            <div className={'field-with-error'}>
                                <div className={'id-input'}>
                                    <span className='input-group-label'>{'Snowflake Schema Name'}:</span>
                                    <input
                                        id={'alternate-source-identifier-input'}
                                        data-testid={"snowflake_schema_name_input"}
                                        className={'input-group-field'}
                                        type='text'
                                        value={snowflakeSchemaName || ''}
                                        onChange={(e) => setSnowflakeSchemaName(e.target.value)}
                                    />
                                </div>
                                {getIssue('snowflake-schema-name-required') && <div className={'error'} data-testid={'snowflake-schema-name-required'}>{getIssue('snowflake-schema-name-required')}</div>}
                            </div>
                        </div>
                        <div id={'projection_checkpoint_dropdown_div'} className={"custom-run-selections-row"}>
                            <div className={'field-with-error'}>
                                <SelectDropdown
                                    id={'projection_checkpoint_dropdown'}
                                    dataTestId={'projection_checkpoint_dropdown'}
                                    inputLabel={"Projection QC Extract"}
                                    onChange={(option) => setProjectionCheckpoint(option.value)}
                                    selectValue={projectionCheckpoint}
                                    selectLabel={getSelectedOption(projectionCheckpoint, checkpointProjectionCheckpointOptions)?.label}
                                    options={checkpointProjectionCheckpointOptions}
                                    isDisabled={createCheckpoint}
                                />
                                {getIssue('projection-qc-extract-required') && <div className={'error'} data-testid={'projection-qc-extract-required'}>{getIssue('projection-qc-extract-required')}</div>}
                                {getIssue('start-date-invalid') && <div className={'error'} data-testid={'start-date-invalid'}>{getIssue('start-date-invalid')}</div>}
                                {getIssue('end-date-invalid') && <div className={'error'} data-testid={'end-date-invalid'}>{getIssue('end-date-invalid')}</div>}
                            </div>
                            <div id={'check-boxes'}>
                                <Checkbox
                                    id={'create_checkpoint'}
                                    labelText={'Create Extract'}
                                    onChange={(checked) => {
                                        setCreateCheckpoint(checked);
                                        if (checked) {
                                        setProjectionCheckpoint(undefined);
                                    } else {
                                        setCheckpointProjectionVersion(undefined);
                                    }
                                    }}
                                    checked={createCheckpoint}/>
                            </div>
                        </div>
                        {createCheckpoint &&
                            <div id={'checkpoint_projection_version_dropdown_div'} className={"custom-run-selections-row"}>
                                <div className={'field-with-error'}>
                                    <SelectDropdown id={"checkpoint_projection_version_dropdown"}
                                                    className={"optional-dropdown"}
                                                    dataTestId={"checkpoint_projection_version"}
                                                    inputLabel={"Extract Projection Version"}
                                                    selectValue={checkpointProjectionVersion}
                                                    options={availableProjectionVersionsOptions}
                                                    onChange={(option) => setCheckpointProjectionVersion(option?.value)}
                                    />
                                    {getIssue('extract-projection-version-required') && <div className={'error'} data-testid={'extract-projection-version-required'}>{getIssue('extract-projection-version-required')}</div>}
                                </div>
                            </div>
                        }
                    </div>
                </div>
                <div className={"custom-run-selections"}>
                    <div className={"custom-run-selections"}>
                        <div className={"custom-run-selections-column"}>
                            <div id={'optional_column_header'} className={"custom-run-selections-row custom-run-section-header"}>
                                <h6>Optional</h6>
                            </div>
                            <div className={'field-with-error'}>
                                <PiEngineSnapshotVersionSelect
                                    options={piEngineSnapshotVersionOptions}
                                    selectedVersion={piEngineSnapshotVersion}
                                    setSelectedVersion={setPiEngineSnapshotVersion}
                                />
                                {getIssue('pi-engine-snapshot-version-required') && <div className={'error'} data-testid={'pi-engine-snapshot-version-required'}>{getIssue('pi-engine-snapshot-version-required')}</div>}
                            </div>
                            <div id={'promo_checkpoint_dropdown_div'} className={"custom-run-selections-row"}>
                                <SelectDropdown id={"promo_checkpoint_dropdown"}
                                                dataTestId={"promo_checkpoint_dropdown"}
                                                inputLabel={"Promo"}
                                                selectValue={promoCheckpointPath}
                                                selectLabel={getSelectedOption(promoCheckpointPath, promoCheckpointOptions)?.label}
                                                options={promoCheckpointOptions}
                                                onChange={(option) => setPromoCheckpointPath(option?.value)}
                                                isClearable={true}
                                />
                            </div>
                            <div id={'stackline_promo_checkpoint_dropdown_div'} className={"custom-run-selections-row"}>
                                <SelectDropdown id={"stackline_promo_checkpoint_dropdown"}
                                                dataTestId={"stackline_promo_checkpoint_dropdown"}
                                                inputLabel={"Stackline Promo"}
                                                selectValue={stacklinePromoCheckpointPath}
                                                selectLabel={getSelectedOption(stacklinePromoCheckpointPath, stacklinePromoCheckpointOptions)?.label}
                                                options={stacklinePromoCheckpointOptions}
                                                onChange={(option) => setStacklinePromoCheckpointPath(option?.value)}
                                                isClearable={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={"trigger-dag-submit-button-container"}>
                <button type="button"
                        className="button primary measures-trigger-submit"
                        onClick={onSubmitButtonClicked}
                >
                    Submit
                </button>
            </div>
        </LoadingWrapper>
        <div className="sld-dag-airflowlogs-container" data-testid="airflow-logs-container">
            <h5>ADP DAG Run Log</h5>
            <AirflowDagLogs processName={[PERIOD_DAG_NAME, WEEKLY_DAG_NAME]} includeDagName={true}/>
        </div>

    </Page>;
};

export default AdpPrivateCloudSnowflakeUpload;